import { useState, useEffect } from 'react';

import { apiService, isPrerendering } from '../services/api';
import { substituteVariables } from '../utils/templateVariables';
import { getStaticData } from '../hooks/useStaticData';


import { Badge } from './ui/badge';
import { Button } from './ui/button';
// import ContactModal from './ContactModal'; // TODO: re-enable if contact form returns

import { CDN_URL } from '@/config/cdn';

const HeroSection = () => {
  // Use static data as initial state to avoid flash of default content
  const [heroContent, setHeroContent] = useState(() =>
    getStaticData('heroContent') || {
      title_line1: "Master Your",
      title_line2: "Inner Warrior",
      subtitle: "Expert martial arts training in Spring Hill, FL. Certified Self-Defense • Kids to Adults • Results-driven approach",
      media_type: "image",
      hero_image_url: `${CDN_URL}/images/hero.png`,
      hero_video_url: null
    }
  );
  const [businessInfo, setBusinessInfo] = useState(() => getStaticData('businessInfo'));
  const [trustIndicators, setTrustIndicators] = useState(() =>
    getStaticData('trustIndicators') || [
      { text: "As seen on News 8, 9 & 10" },
      { text: "Certified Self-Defense" },
      { text: "★★★★★ Google-Rated" }
    ]
  );
  useEffect(() => {
    // Skip API calls during pre-rendering (react-snap)
    if (isPrerendering()) return;

    const fetchHeroContent = async () => {
      const content = await apiService.getHeroContent();
      if (content) setHeroContent(content);
    };
    const fetchBusinessInfo = async () => {
      const info = await apiService.getBusinessInfo();
      if (info) setBusinessInfo(info);
    };
    const fetchTrustIndicators = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trust-indicators`);
        if (response.ok) {
          const data = await response.json();
          setTrustIndicators(data);
        }
      } catch (error) {
        console.error('Failed to fetch trust indicators');
      }
    };
    fetchHeroContent();
    fetchBusinessInfo();
    fetchTrustIndicators();
  }, []);

  const handleBooking = () => {
    window.location.href = 'tel:+13524280086';
  };

  return (
    <section className="relative min-h-[85vh] md:min-h-[90vh] min-h-[85svh] md:min-h-[90svh] flex items-center justify-center overflow-hidden">
      {/* Background Media (Image or Video) with Overlay */}
      <div className="absolute inset-0 z-0">
        {heroContent.media_type === 'video' && heroContent.hero_video_url ? (
          <div className="relative w-full h-full">
            <iframe
              src={`${heroContent.hero_video_url}?autoplay=1&mute=1&loop=1&controls=0&modestbranding=1&showinfo=0&rel=0&playlist=${heroContent.hero_video_url.split('/embed/')[1]?.split('?')[0]}`}
              className="w-full h-full object-cover"
              allow="autoplay; encrypted-media"
              frameBorder="0"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '100vw',
                height: '56.25vw',
                minHeight: '100dvh',
                minWidth: '177.77vh',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none'
              }}
              title="Hero Background Video"
            ></iframe>
          </div>
        ) : (
          <img
            src={heroContent.hero_image_url}
            alt="Owaza Martial Arts training"
            className="w-full h-full object-cover"
            fetchpriority="high"
            loading="eager"
            decoding="async"
          />
        )}
        <div className="absolute inset-0 bg-black/60"></div>
      </div>

      {/* Hero Content */}
      <div className="relative z-10 container mx-auto px-4 py-12 md:py-20">
        <div className="max-w-4xl mx-auto text-center space-y-6 md:space-y-8">
          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-6 md:mb-8">
            {trustIndicators.map((indicator, index) => (
              <Badge
                key={indicator.id || index}
                variant="secondary"
                className="bg-white/90 text-gray-900 px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-semibold backdrop-blur-sm shadow-lg"
              >
                {substituteVariables(indicator.text, businessInfo)}
              </Badge>
            ))}
          </div>

          {/* Main Headline */}
          <div className="space-y-2 md:space-y-4">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight">
              <span className="block">{heroContent.title_line1}</span>
              <span className="block text-red-500">{heroContent.title_line2}</span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-100 max-w-3xl mx-auto leading-relaxed px-4">
              {heroContent.subtitle}
            </p>
          </div>

          {/* Notice Box */}
          <div className="bg-yellow-500/20 backdrop-blur-sm border border-yellow-400/30 rounded-lg p-3 md:p-4 max-w-2xl mx-auto">
            <p className="text-white text-sm md:text-base font-semibold">
              <strong>Appointment only.</strong> Classes are closed to spectators.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center pt-4 md:pt-6 px-2 sm:px-4">
            <Button
              size="lg"
              className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white font-bold px-4 sm:px-6 md:px-8 py-4 md:py-6 text-sm sm:text-base md:text-lg shadow-2xl transform hover:scale-105 transition-all duration-200"
              onClick={handleBooking}
            >
              Book Your Appointment
            </Button>

            <Button
              size="lg"
              variant="outline"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-gray-900 font-semibold px-4 sm:px-6 md:px-8 py-4 md:py-6 text-sm sm:text-base md:text-lg transition-all duration-200"
              onClick={() => window.location.href = `tel:${businessInfo?.phone?.replace(/[^0-9+]/g, '') || '+13524280086'}`}
            >
              Call {businessInfo?.phone || '(352) 428-0086'}
            </Button>
          </div>

        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white/70 rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>

      {/* ContactModal removed — all CTAs now call Jim Rivera's direct line tel:+13524280086 */}
      {/* TODO: re-enable <ContactModal> here if contact form returns */}
    </section>
  );
};

export default HeroSection;
